import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { OrderItemWithBOM, OrderWithPackagingList } from '../../../../backend/src/orders/order-interfaces';
import { NotificationService } from '../notification/notification.service';
import { Carrier } from '../../../../backend/src/carriers/entities/carrier.entity';
import { FormControl, FormGroup } from '@angular/forms';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ComponentBomDescriptor } from '../../../../backend/src/components/component-bom-descriptor.interface';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { LabelDialogService } from '../label-dialog/label-dialog.service';
import { openMarkerLabel } from './marker-label';
import { getCalendarDayDifference } from '../utils/order-deadline.util';
import { OrderMessage, OrderMessagesService } from '../stokker/api';
import { AuthService } from '../auth/auth.service';
import { userHasMenu } from '../auth/menu-access';
import { StockReallocProduct } from '../stokker/stock-realloc/stock-realloc.component';

interface OrderItemWithBomView extends OrderItemWithBOM {
  component?: {
    id?: number;
    name?: string;
    sku?: string;
    image_file?: string;
    is_custom?: number;
  } | null;
}

interface OrderView extends OrderWithPackagingList {
  orderProducts: OrderItemWithBomView[];
}

interface FulfillmentUnitDraft {
  componentId?: number;
  /** komponens nélküli tétel (ismeretlen/üres SKU): a pipa az order_product.id-ra épül */
  orderProductId?: number;
  name: string;
  sku: string;
  quantity: number;
  imageFile?: string;
  weightGrams?: number;
  printMinutes?: number;
  materialName?: string | null;
}

interface FulfillmentUnitSummary extends FulfillmentUnitDraft {
  packagedQuantity: number;
}

interface FulfillmentRow {
  key: string;
  /** order_product.id — a tétel törléséhez/visszaállításához */
  productId?: number | null;
  /** törölt tétel: szürkén marad, de kimarad a számlából és a szállításból */
  removed: boolean;
  /** utólag hozzáadott tétel (nem kerül a számlára/szállítmányba) */
  isAdded: boolean;
  name: string;
  sku: string;
  quantity: number;
  imageFile?: string;
  unitSummaries: FulfillmentUnitSummary[];
  isPackaged: boolean;
  canExpand: boolean;
  canTogglePackaging: boolean;
  personalization?: string | null;
  /** Egyedi (rendelésre gyártott) komponens-e — a badge-hez. */
  custom?: boolean;
  // Készlet-státusz (az átruházáshoz): a komponens szabad készlete alapján.
  componentId?: number;
  availableStock?: number | null;
  stockOut?: boolean;
  lastPiece?: boolean;
  /** összes anyag súlya grammban (BOM levelekből) */
  grams: number;
  /** összes nyomtatási idő percben (BOM levelekből) */
  mins: number;
}

// A csomag-típus az Anyagok „Csomagolás" listájából (box anyagok) jön — a
// nevek dinamikusak, ezért string.
type PkgType = string;

interface ReplacementDraft {
  sel: Record<string, boolean>;
  reason: string | null;
}

interface ReplacementPackage {
  /** backend id (order_replacement.id) */
  id: number;
  /** a saját súlya alapján lekért valós szállítási díj (díjszabásból) */
  quote?: { zoneName: string; weightBracketGrams: number; totalNet: number } | null;
  title: string;
  detail: string;
  pkg: PkgType;
  weight: string;
  label: boolean;
  /** a csere csomag SAJÁT címkéje (order_replacement.label_url) — nem a rendelésé */
  labelUrl: string | null;
  /** a csere csomag saját küldeménykódja + futárja */
  trackingCode: string | null;
  carrierName: string | null;
  grams: number;
  mins: number;
  skus: string[];
}

/** A backend 'torott' | 'kimaradt' | 'egyeb' kulcsai ↔ UI feliratok. */
const REPLACEMENT_REASON_KEYS: Record<string, string> = {
  'Törött': 'torott',
  'Kimaradt': 'kimaradt',
  'Egyéb': 'egyeb',
};
const REPLACEMENT_REASON_LABELS: Record<string, string> = {
  torott: 'Törött',
  kimaradt: 'Kimaradt',
  egyeb: 'Egyéb',
};

interface FinanceRow {
  label: string;
  value: string;
  negative: boolean;
}

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.scss'
})
export class OrderDetailsComponent implements OnInit, OnDestroy {

  orderId: number;
  order: OrderView;
  fetching = false;
  showAddressEditor = false;
  savingPackaging = false;
  preferredCarrier = {} as Carrier;
  generatingLabel = false;
  generatingInvoice = false;
  cancellingInvoice = false;
  savingAddress = false;
  activeRowActionKey: string | null = null;
  expandedRows: Record<string, boolean> = {};
  fulfillmentRows: FulfillmentRow[] = [];

  // Komponens-készlet SKU szerint (szabad készlet + foglalás) — a tétel-szintű
  // „Nincs készleten" / „Utolsó darab!" jelöléshez és az átruházáshoz.
  private stockBySku: Record<string, { id: number; stock: number; reserved: number }> = {};
  // Az átruházás-modálnak átadott termék (null = zárva).
  reallocProduct: StockReallocProduct | null = null;
  addressForm: FormGroup = new FormGroup({
    customer: new FormControl(''),
    customer_email: new FormControl(''),
    customer_phone: new FormControl(''),
    shipping_country: new FormControl(''),
    shipping_postal_code: new FormControl(''),
    shipping_city: new FormControl(''),
    shipping_address_line1: new FormControl(''),
    shipping_address_line2: new FormControl(''),
  });

  // --- Csomag adatok — a doboz-opciók az Anyagok „Csomagolás" listájából
  // (box típusú material) töltődnek, nem beégetve. A pkgTypes/pkgPresets a
  // betöltéskor felülíródik; a lenti értékek csak fallback, amíg megjön a lista.
  pkgTypes: PkgType[] = ['A14', 'A15', 'Boríték'];
  pkgPresets: Record<PkgType, string> = {
    'A14': '15 × 20 × 8 cm',
    'A15': '20 × 25 × 10 cm',
    'Boríték': '32 × 23 × 2 cm',
  };
  // Ajánlott szállítási díj (EUR) doboztípusonként — carrier-becslés, fallback.
  pkgCosts: Record<PkgType, number> = { 'A14': 4.20, 'A15': 5.60, 'Boríték': 2.90 };
  /** csomagolóanyag beszerzési ára doboztípusonként, Ft/db (Anyagok → Csomagolás) */
  private boxPricesHuf: Record<string, number> = {};
  /** filament ára anyagnév szerint, Ft/kg (Anyagok → Filament) */
  private filamentPricesHuf: Record<string, number> = {};
  pkgType: PkgType = 'A14';
  pkgWeight = '';

  // --- Valós szállítási díj a díjszabásból (Szállítók → díjszabás) ---
  /** a csomag súlya + célország alapján kapott árajánlat (Ft-ban) */
  shippingQuote: { zoneName: string; weightBracketGrams: number; totalNet: number;
    shippingFee: number; customsFee: number; surchargeAmount: number } | null = null;
  /** 1 EUR = ennyi Ft (MNB árfolyam, tartalék: ECB / kézi beállítás) */
  private hufPerEur = 400;
  /** az árfolyam forrása és napja — a felületen tooltipben mutatjuk */
  exchangeRateSource = '';
  exchangeRateDate = '';
  private pkgWeightTouched = false;

  // --- Cseretermék (csere csomag) flow — a prototípus draft/replacements logikája ---
  readonly replacementReasons = ['Törött', 'Kimaradt', 'Egyéb'];
  replacementDraft: ReplacementDraft | null = null;
  replacements: ReplacementPackage[] = [];

  // --- Pénzügyek panel ---
  finOpen = false;
  finRows: FinanceRow[] = [];
  profitValue = '';
  profitPct = '';
  profitNegative = false;
  /** a panel alján a számítás magyarázata — a tényleg használt kulcsokkal */
  finNote = '';

  // Fallback konstansok, amíg az Anyagok / Beállítások adatai megjönnek:
  // anyag 3200 Ft/kg PLA ≈ 0,008 EUR/g, nyomtatás + áram ≈ 0,0037 EUR/perc.
  private readonly MATERIAL_COST_PER_GRAM = 0.008;
  private readonly PRINT_COST_PER_MINUTE = 0.0037;

  // --- Kalkulációs beállítások (Beállítások képernyő) ---
  /** ÁFA-kulcs országkód szerint, % */
  private vatRates: Record<string, number> = {};
  /** vámkulcs országkód szerint, % */
  private dutyRates: Record<string, number> = {};
  /** gépidő ár, Ft/perc */
  private printPerMinHuf: number | null = null;
  /** Webshop-díjak (Beállítások → Kalkuláció): %-os és fix díjak boltonként. */
  private feeShops: Array<{ name: string; txn: number; pay: number; fixed: number; listing: number }> = [];
  /** Egységnyi önköltség-bontás (Ft) komponensenként — a Készlet oldallal azonos számítás. */
  private unitCosts: Record<number, { cost: number; material: number; machine: number; bought: number; grams: number; minutes: number; missing: boolean; estimated: boolean }> = {};
  private unitCostsKey = '';

  // --- Üzenőfal ---
  messages: OrderMessage[] = [];
  msgInput = '';
  loadingMessages = false;
  sendingMessage = false;
  private deletingMessageIds = new Set<number>();

  // Csomag adatok / csere csomag súly mentése gépelés közben (debounce-olva)
  private packageSave$ = new Subject<void>();
  private replacementSave$ = new Subject<number>();
  private subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private confirmDialog: ConfirmDialogService,
    private labelDialog: LabelDialogService,
    private orderMessagesService: OrderMessagesService,
    private auth: AuthService,
  ) { }

  /** „Chatbe" gomb: a rendelés megosztása a csapatchatben */
  get canChat(): boolean {
    return userHasMenu(this.auth.user, 'Chat');
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.packageSave$.pipe(debounceTime(500)).subscribe(() => this.savePackage()),
    );
    this.subscriptions.add(
      this.replacementSave$
        .pipe(debounceTime(500))
        .subscribe((index) => this.saveReplacement(index)),
    );

    this.fetchBoxOptions();
    this.fetchComponentStock();
    this.fetchExchangeRate();
    this.fetchCalculationSettings();

    // FONTOS: a route-feliratkozást is le kell zárni a komponenssel együtt.
    // Enélkül a megsemmisített („zombi") példányok tovább figyelik az
    // útvonalat, és a saját, régi orderId-jükkel küldenek kéréseket — emiatt
    // ment korábban a csere-csomag mentése egy már nem létező rendelésre.
    this.subscriptions.add(
      this.route.params.subscribe(paramMap => {
        this.orderId = parseInt(paramMap['id']);
        if(this.orderId > 0) {
          this.replacementDraft = null;
          this.replacements = [];
          this.pkgWeightTouched = false;
          this.fetchTheOrder();
          this.fetchMessages();
          this.fetchReplacements();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  fetchTheOrder() {
    this.fetching = true;
    this.http.get<OrderWithPackagingList>(`orders/${this.orderId}/with-content`).subscribe(res => {
      this.order = res as OrderView;
      this.initAddressForm();
      this.rebuildFulfillmentRows();
      this.initPackageData();
      this.fetchShippingQuote();
      this.rebuildReplacementCosts();
      this.fetchAllReplacementQuotes();
      this.recomputeFinance();
      this.fetching = false;
      if(this.order?.country_iso) {
        this.fetchPreferredCarrier();
      }
    }, err => {
      this.fetching = false;
      this.notificationService.show(err.error.message, 'danger');
    })
  }

  getDueDay() {
    return getCalendarDayDifference(this.order?.shipping_expected_at);
  }

  getDueLabel(): string | null {
    const days = this.getDueDay();
    if (days === null) {
      return null;
    }
    if (days > 0) {
      return `${days} nap van hátra`;
    }
    if (days === 0) {
      return 'Ma esedékes';
    }
    return `${Math.abs(days)} napja késik`;
  }

  getDueTone(): 'ok' | 'today' | 'late' {
    const days = this.getDueDay();
    if (days === null || days > 0) {
      return 'ok';
    }
    return days === 0 ? 'today' : 'late';
  }

  isOrderShipped(): boolean {
    return this.order?.is_shipped === 1 || this.order?.workflow_status === 'fulfilled';
  }

  areAllComponentsPackaged(): boolean {
    if (!this.order) {
      return false;
    }

    return (this.order.packagingComponentList || []).every((component) => component.packaged);
  }

  isPackagingCompleted(): boolean {
    return this.order?.packaging_ready === 1 || this.isOrderShipped();
  }

  isCompleteButtonDisabled(): boolean {
    return this.savingPackaging || this.isPackagingCompleted() || !this.areAllComponentsPackaged();
  }

  getOrderStateLabel(): string {
    if (this.order?.workflow_status === 'fulfilled') return 'Teljesítve';
    if (this.order?.workflow_status === 'label_generated') return 'Címke generálva';
    if (this.order?.workflow_status === 'archived') return 'Archiválva';
    return 'Új rendelés';
  }

  getStatePillClass(): string {
    if (this.order?.workflow_status === 'fulfilled') return 'stk-pill--green';
    if (this.order?.workflow_status === 'label_generated') return 'stk-pill--info';
    if (this.order?.workflow_status === 'archived') return 'stk-pill--grey';
    return 'stk-pill--yellow';
  }

  getMerchantName(): string {
    if (this.order?.source_key === 'manual') {
      return 'Manuál';
    }
    if (this.isJumpOrder()) {
      return 'Jump';
    }
    if (this.order?.source_key === 'medusa') {
      return 'Webshop';
    }
    return this.order?.merchant?.name || this.resolveMerchantName(this.order?.merchant_id);
  }

  isJumpOrder(): boolean {
    return this.order?.merchant_id === 1 && this.order?.etsy_shop_id === 2;
  }

  getRecommendedCarrierName(): string {
    return this.preferredCarrier?.name || this.order?.carrier_name || 'Nincs megadva';
  }

  getCustomerMessage(): string {
    const customerMessage = this.order?.customer_message?.trim();
    return customerMessage?.length ? customerMessage : 'Nincs vásárlói üzenet';
  }

  getPaymentLine(): string {
    const payment = this.order?.payment_method
      ? `Fizetve · ${this.order.payment_method}`
      : 'Fizetési mód nincs megadva';
    const message = this.order?.customer_message?.trim()?.length
      ? 'vásárlói üzenet'
      : 'nincs üzenet';
    return `${payment} · ${message}`;
  }

  getEtsyBuyerEmail(): string | null {
    if (!this.isEtsyOrder()) {
      return null;
    }

    const email = this.order?.customer_email?.trim();
    return email?.length ? email : null;
  }

  // ---------------------------------------------------------------------------
  // Címzett megjelenítés
  // ---------------------------------------------------------------------------

  getAddrStreetLines(): string[] {
    const lines = [
      this.order?.shipping_address_line1 || this.order?.address,
      this.order?.shipping_address_line2,
    ];
    return lines.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  }

  getAddrCityLine(): string {
    const cityPart = [this.order?.shipping_postal_code, this.order?.shipping_city]
      .filter(Boolean)
      .join(' ');
    const country = this.order?.shipping_country || this.order?.country_iso;
    return [cityPart, country].filter(Boolean).join(' · ');
  }

  hasInvoice(): boolean {
    return !!(this.order?.billingo_invoice_id || this.order?.billingo_invoice_download_url);
  }

  hasLabel(): boolean {
    return !!(this.order?.shippypro_label_url || this.order?.tracking_code);
  }

  getTrackingCode(): string | null {
    return this.order?.tracking_code || this.order?.shippypro_tracking_number || null;
  }

  isInvoiceCancelled(): boolean {
    return this.order?.billingo_invoice_cancelled === 1;
  }

  getInvoiceChipLabel(): string {
    if (this.isInvoiceCancelled()) {
      return 'Sztornózva';
    }
    return this.hasInvoice() ? 'Létrehozva' : 'Nincs';
  }

  getLabelChipLabel(): string {
    if (this.generatingLabel) {
      return 'Generálás…';
    }
    if (this.order?.is_shipped === 1) {
      return 'Feladva';
    }
    return this.hasLabel() ? 'Létrehozva' : 'Nincs';
  }

  getLabelSummary(): string {
    const type = this.order?.label_type || 'A4';
    return `Címke: ${type} · ${this.hasLabel() ? 'létrehozva' : 'még nincs generálva'}`;
  }

  getRecommendedShippingLine(): string {
    const quoted = this.quotedShippingEur();
    if (quoted !== null && this.shippingQuote) {
      // Valós díjszabás: zóna + súlysáv szerinti ár.
      return `${this.getRecommendedCarrierName()} · ${this.shippingQuote.zoneName}`
        + ` · ${this.shippingQuote.weightBracketGrams} g sáv · ${this.formatMoney(quoted)}`;
    }
    return `Ajánlott: ${this.getRecommendedCarrierName()} · ${this.formatMoney(this.pkgCostOf(this.pkgType))} (becsült)`;
  }

  toggleAddressEditor() {
    this.showAddressEditor = !this.showAddressEditor;
    if (!this.showAddressEditor) {
      // Mégse / bezárás: a nem mentett módosításokat el kell dobni, különben
      // az űrlap újranyitáskor a korábbi (mentetlen) értékeket mutatná.
      this.initAddressForm();
    }
  }

  initAddressForm() {
    this.addressForm = new FormGroup({
      customer: new FormControl(this.order?.customer || ''),
      customer_email: new FormControl(this.order?.customer_email || ''),
      customer_phone: new FormControl(this.order?.customer_phone || ''),
      shipping_country: new FormControl(this.order?.shipping_country || this.order?.country_iso || ''),
      shipping_postal_code: new FormControl(this.order?.shipping_postal_code || ''),
      shipping_city: new FormControl(this.order?.shipping_city || ''),
      shipping_address_line1: new FormControl(this.order?.shipping_address_line1 || this.order?.address || ''),
      shipping_address_line2: new FormControl(this.order?.shipping_address_line2 || ''),
    });
  }

  toggleRowExpansion(row: FulfillmentRow) {
    if (!row.canExpand) {
      return;
    }
    this.expandedRows[row.key] = !this.expandedRows[row.key];
  }

  isRowExpanded(row: FulfillmentRow): boolean {
    return !!this.expandedRows[row.key];
  }

  isRowActionPending(row: FulfillmentRow): boolean {
    return this.activeRowActionKey === row.key;
  }

  toggleFulfillmentRow(row: FulfillmentRow) {
    if (!row.canTogglePackaging || this.activeRowActionKey) {
      return;
    }

    const shouldPackage = !row.isPackaged;
    const requests = row.unitSummaries.flatMap((unit) => {
      if (!unit.componentId && !unit.orderProductId) {
        return [];
      }

      const requestCount = shouldPackage
        ? unit.quantity - unit.packagedQuantity
        : unit.packagedQuantity;

      return Array.from({ length: requestCount }, () =>
        this.http.post('orders/package-component', {
          order_id: this.orderId,
          component_id: unit.componentId ?? null,
          // komponens nélküli tétel: a rendelési tétel azonosítójával
          order_product_id: unit.componentId ? null : (unit.orderProductId ?? null),
          is_packaged: shouldPackage
        }),
      );
    });

    if (!requests.length) {
      return;
    }

    this.activeRowActionKey = row.key;
    forkJoin(requests).subscribe(() => {
      this.activeRowActionKey = null;
      this.notificationService.show(
        shouldPackage ? 'Tétel készre jelölve.' : 'Tétel újranyitva.',
        'success',
        900
      );
      this.fetchTheOrder();
    }, err => {
      this.activeRowActionKey = null;
      this.notificationService.show(`Csomagolás sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  getPackedRowCount(): number {
    return this.fulfillmentRows.filter((row) => row.isPackaged).length;
  }

  getPackedPercent(): number {
    if (!this.fulfillmentRows.length) {
      return 0;
    }
    return Math.round((this.getPackedRowCount() / this.fulfillmentRows.length) * 100);
  }

  getProgressLabel(): string {
    return `${this.getPackedRowCount()} / ${this.fulfillmentRows.length} tétel becsomagolva`;
  }

  packagingReady(is_packaged: boolean) {
    // Check if every component is packaged in the packagingComponentList
    const allComponentsPackaged = this.order.packagingComponentList?.every((component) => component.packaged);
    if(!allComponentsPackaged && is_packaged) {
      this.notificationService.show('Nincs minden egység becsomagolva!', 'warning');
      return;
    }
    this.savingPackaging = true;
    this.http.post(`orders/packaging-ready`, {
      order_id: this.orderId,
      is_packaged: is_packaged
    }).subscribe(res => {
      this.savingPackaging = false;
      if(is_packaged) {
        this.notificationService.show('Csomagolás kész!', 'success', 500);
        this.order.packaging_ready = 1;
      } else {
        this.notificationService.show('Csomagolás visszavonva!', 'success');
        this.order.packaging_ready = 0;
      }
      // Visszavonáskor a szerver az is_shipped / workflow_status mezőt is
      // visszaállítja — újratöltés nélkül a fejléc „Teljesítve” maradna.
      this.fetchTheOrder();
    }, err => {
      this.savingPackaging = false;
      this.notificationService.show(`Csomagolás sikertelen! Részletek: ${err.error.message}`, 'danger');
    })
  }

  requestPackagingReady(is_packaged: boolean) {
    if (is_packaged && this.isPackagingCompleted()) {
      return;
    }

    if (is_packaged && !this.areAllComponentsPackaged()) {
      this.notificationService.show('Nincs minden egység becsomagolva!', 'warning');
      return;
    }

    this.confirmDialog.open({
      title: is_packaged ? 'Rendelés teljesítése' : 'Csomagolás visszavonása',
      message: is_packaged
        ? 'Biztosan készre jelölöd a rendelés csomagolását?'
        : 'Biztosan visszavonod a csomagolás kész állapotát?',
      details: is_packaged
        ? 'A rendelés ezután teljesítettként jelenik meg a részletezőn.'
        : 'A rendelés újra nyitott csomagolási állapotba kerül.',
      confirmLabel: is_packaged ? 'Készre jelölöm' : 'Visszavonás',
      tone: is_packaged ? 'success' : 'warning',
      icon: is_packaged ? 'fa-solid fa-circle-check' : 'fa-solid fa-rotate-left'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.packagingReady(is_packaged);
    });
  }

  fetchPreferredCarrier() {
    this.http.get<Carrier>(`carriers?country=${this.order.country_iso}`).subscribe((carrier) => {
      this.preferredCarrier = carrier || ({} as Carrier);
      if (carrier?.name) {
        this.order.carrier_name = carrier.name;
      }
      // A Pénzügyek panel címke-sora a szállító nevét használja — a szállító
      // aszinkron érkezik, ezért az érkezésekor újra kell számolni, különben
      // ott „Nincs megadva címke” marad.
      this.recomputeFinance();
    }, e => {
      this.notificationService.show(`Hiba a preferált szállító betöltése során! Részletek: ${e.message}`, 'warning');
    });
  }

  saveAddress() {
    this.savingAddress = true;
    this.http.patch(`orders/${this.orderId}/shipping-address`, this.addressForm.value).subscribe((updatedOrder: any) => {
      this.savingAddress = false;
      this.order = { ...this.order, ...updatedOrder };
      this.showAddressEditor = false;
      this.notificationService.show('Szállítási cím mentve, ShippyPro frissítve.', 'success');
    }, err => {
      this.savingAddress = false;
      this.notificationService.show(`Cím mentése sikertelen! Részletek: ${err.error.message}`, 'danger');
    });
  }

  requestLabelGeneration(regenerate = false) {
    this.confirmDialog.open({
      title: regenerate ? 'Címke újragenerálása' : 'Címke generálása',
      message: regenerate
        ? 'Biztosan újragenerálod a szállítási címkét ehhez a rendeléshez?'
        : 'Biztosan legenerálod a szállítási címkét ehhez a rendeléshez?',
      details: regenerate
        ? 'A friss címke új lapon nyílik meg, és a korábbi verzió felülíródhat.'
        : 'Sikeres generálás után a címke automatikusan új lapon nyílik meg.',
      confirmLabel: regenerate ? 'Újragenerálás' : 'Címke generálása',
      tone: regenerate ? 'warning' : 'primary',
      icon: 'fa-solid fa-tag'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.generateLabel(regenerate);
    });
  }

  /**
   * Cseretermék-címke generálása. Ha replacementIndex is érkezik, sikeres
   * generálás után a hozzá tartozó csere csomag kártya címkéje "Létrehozva"
   * állapotra vált.
   */
  requestReplacementLabelGeneration(replacementIndex?: number) {
    this.confirmDialog.open({
      title: 'Cseretermék küldése',
      message: 'Biztosan cseretermék-címkét generálsz ehhez a rendeléshez?',
      details:
        'A címke warranty replacement küldeményként készül: a termék neve ' +
        '"Warranty replacement – 3D printed product – no charge to customer", ' +
        'a vevő által fizetett összeg 0.00 EUR, proforma számlatípussal – ' +
        'hogy a vevőt ne terhelje újra a vám.',
      confirmLabel: 'Cseretermék küldése',
      tone: 'warning',
      icon: 'fa-solid fa-arrows-rotate'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.generateLabel(true, true, replacementIndex);
    });
  }

  generateLabel(regenerate = false, replacement = false, replacementIndex?: number) {
    this.generatingLabel = true;
    // A csere csomag kódja a SAJÁT sorára kerül, nem a rendelésre — ezért kell
    // átadni, MELYIK csere csomaghoz készül a címke. Enélkül a szerver a
    // legutóbbi, még kód nélküli csomaghoz köti, ami több nyitott csere
    // csomagnál rossz sort találhat el.
    const replacementId = replacement && replacementIndex !== undefined
      ? this.replacements[replacementIndex]?.id
      : undefined;
    this.http.post(`orders/${this.orderId}/generate-label`, {
      regenerate,
      replacement,
      replacementId,
    }).subscribe((response: any) => {
      this.generatingLabel = false;
      // A csere csomag címkéje a SAJÁT sorára kerül, a rendelés fő címkéje
      // érintetlen marad. A válaszból ezért külön vesszük ki, melyik csere
      // csomag kapta a friss (warranty, ~0 értékű) címkét — és AZT nyitjuk meg,
      // nem a rendelés régi, teljes árú címkéjét, amire újra vámot számolnának.
      const { replacement_label: replacementLabel, ...updatedOrder } = response || {};
      this.order = { ...this.order, ...updatedOrder };
      this.notificationService.show(
        replacement ? 'Cseretermék-címke generálva.' : regenerate ? 'Címke újragenerálva.' : 'Címke generálva.',
        'success',
      );
      if (replacement && replacementLabel?.replacement_id) {
        // A szerver már elmentette a címke állapotát és a küldeménykódot a
        // csere csomagra — a kártyák innen frissülnek.
        this.fetchReplacements();
        if (replacementLabel.label_url) {
          this.openLabelDocument(Number(replacementLabel.replacement_id));
        }
        return;
      }
      if(updatedOrder?.shippypro_label_url) {
        this.openLabelDocument();
      }
    }, err => {
      this.generatingLabel = false;
      this.notificationService.show(`Címkegenerálás sikertelen! Részletek: ${err.error.message}`, 'danger');
    });
  }

  /**
   * Jelölő címke (vészmegoldás): ha a futár nem ad címkét, a csomagot az ő
   * kérésükre feladó + rendelésszám + címzett adatokkal jelölve adjuk le.
   */
  printMarkerLabel() {
    const order: any = this.order || {};
    const iso = `${order.country_iso || order.shipping_country || ''}`.trim().toUpperCase();
    let country = iso;
    try {
      const name = iso ? new Intl.DisplayNames(['en'], { type: 'region' }).of(iso) : '';
      country = name && name !== iso ? `${name} (${iso})` : iso;
    } catch { /* régi böngésző: marad az ISO kód */ }
    const line1 = `${order.shipping_address_line1 || ''}`.trim();
    const addressLines = line1
      ? [line1, `${order.shipping_address_line2 || ''}`.trim(), `${order.shipping_postal_code || ''} ${order.shipping_city || ''}`.trim()]
      : `${order.address || ''}`.split('\n').map((line: string) => line.trim());
    const items = (order.orderProducts || [])
      .filter((p: any) => !p?.removed_at)
      .map((p: any) => ({
        quantity: Number(p.quantity) || 1,
        text: [p.sku, p.name].filter(Boolean).join(' – '),
      }));
    const opened = openMarkerLabel({
      orderRef: `${order.receipt_id || order.order_number || order.id || this.orderId}`,
      shop: this.getMerchantName(),
      name: `${order.customer || ''}`.trim(),
      addressLines: addressLines.filter((line: string) => line && line !== '-'),
      country,
      phone: `${order.customer_phone || ''}`.trim(),
      email: `${order.customer_email || ''}`.trim(),
      items,
      packageType: order.package_type || null,
      weightGrams: Number(order.package_weight_grams) || null,
    });
    if (!opened) {
      this.notificationService.show('A böngésző letiltotta az új lapot — engedélyezd a felugró ablakokat.', 'warning');
    }
  }

  /**
   * UPS címke (közvetlen API) — külön gomb; az alapértelmezett Spring GDS a
   * megszokott címke-gombon marad. replacementIndex-szel a csere csomagé.
   */
  openUpsLabel(replacementIndex?: number) {
    const replacement = replacementIndex !== undefined;
    const replacementId = replacement ? this.replacements[replacementIndex as number]?.id : undefined;
    this.labelDialog.open({
      orderId: Number(this.orderId),
      reference: `${this.order?.order_number || this.order?.receipt_id || this.orderId}`,
      replacement,
      replacementId,
    }).subscribe((result) => {
      if (!result?.response) {
        return;
      }
      const { replacement_label: replacementLabel, ups, ...updatedOrder } = result.response || {};
      this.order = { ...this.order, ...updatedOrder };
      const cost = ups?.total != null ? `, ${Number(ups.total).toFixed(2)} ${ups.currency || ''}`.trimEnd() : '';
      this.notificationService.show(`UPS címke generálva (${ups?.service || 'UPS'}${cost}).`, 'success');
      (ups?.warnings || []).forEach((warning: string) => this.notificationService.show(warning, 'warning'));
      this.recomputeFinance();
      if (replacement && replacementLabel?.replacement_id) {
        this.fetchReplacements();
        this.openLabelDocument(Number(replacementLabel.replacement_id));
        return;
      }
      if (updatedOrder?.shippypro_label_url) {
        this.openLabelDocument();
      }
    });
  }

  /**
   * Címke PDF megnyitása új lapon. replacementId-vel a csere csomag SAJÁT
   * címkéje nyílik meg; enélkül a rendelés fő címkéje.
   */
  openLabelDocument(replacementId?: number) {
    const previewWindow = window.open('', '_blank');
    const documentUrl = replacementId
      ? `orders/${this.orderId}/replacements/${replacementId}/label/document`
      : `orders/${this.orderId}/label/document`;

    this.http.get(documentUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        if (previewWindow) {
          previewWindow.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank');
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      },
      error: (err) => {
        if (previewWindow) {
          previewWindow.close();
        }
        this.notificationService.show(`Címke megnyitása sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      }
    });
  }

  /**
   * Van-e a csere csomaghoz megnyitható címke. Az újabb csere csomagoknak saját
   * címke URL-jük van; a régieknél (a saját küldeménykód bevezetése előtt) a
   * címke a rendelésre íródott, ott csak a rendelés címkéje érhető el.
   */
  hasReplacementLabelDocument(replacement: ReplacementPackage): boolean {
    if (replacement.labelUrl) {
      return true;
    }
    return replacement.label && !!this.order?.shippypro_label_url;
  }

  openReplacementLabelDocument(replacement: ReplacementPackage) {
    if (replacement.labelUrl) {
      this.openLabelDocument(replacement.id);
      return;
    }
    this.openLabelDocument();
  }

  requestInvoiceGeneration() {
    this.confirmDialog.open({
      title: 'Számla generálása',
      message: 'Biztosan létrehozod a számlát ehhez a rendeléshez?',
      details: 'A sikeres generálás után a számla letölthető lesz az oldalról.',
      confirmLabel: 'Számla generálása',
      tone: 'primary',
      icon: 'fa-solid fa-file-invoice'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.generateInvoice();
    });
  }

  generateInvoice() {
    this.generatingInvoice = true;
    this.http.post(`orders/${this.orderId}/invoice/generate`, {}).subscribe((updatedOrder: any) => {
      this.generatingInvoice = false;
      this.order = { ...this.order, ...updatedOrder };
      this.notificationService.show('Számla generálva.', 'success');
    }, err => {
      this.generatingInvoice = false;
      this.notificationService.show(`Számlagenerálás sikertelen! Részletek: ${err.error.message}`, 'danger');
    });
  }

  downloadInvoice() {
    this.http.get<{download_url: string | null; document_url?: string | null; provider?: string}>(`orders/${this.orderId}/invoice/download`).subscribe((res) => {
      if(res?.download_url) {
        window.open(res.download_url, '_blank');
      } else if (res?.document_url) {
        // Okos Könyvelőm: nincs publikus számlakép, a PDF-et a backend adja ki (l. openInvoiceDocument).
        this.openInvoiceDocument();
      } else {
        this.notificationService.show('Nincs letölthető számla URL.', 'warning');
      }
    }, err => {
      this.notificationService.show(`Számla letöltés sikertelen! Részletek: ${err.error.message}`, 'danger');
    });
  }

  private openInvoiceDocument() {
    const previewWindow = window.open('', '_blank');
    this.http.get(`orders/${this.orderId}/invoice/document`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        if (previewWindow) {
          previewWindow.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank');
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      },
      error: (err) => {
        previewWindow?.close();
        this.notificationService.show(`Számla letöltés sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      },
    });
  }

  cancelInvoice() {
    this.confirmDialog.open({
      title: 'Számla sztornózása',
      message: 'Biztosan sztornózod a már elkészült számlát?',
      details: 'Ez a művelet nem vonható vissza.',
      confirmLabel: 'Sztornózás',
      tone: 'danger',
      icon: 'fa-solid fa-file-circle-xmark'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.performInvoiceCancel();
    });
  }

  private performInvoiceCancel() {
    this.cancellingInvoice = true;
    this.http.post(`orders/${this.orderId}/invoice/cancel`, {}).subscribe((updatedOrder: any) => {
      this.cancellingInvoice = false;
      this.order = { ...this.order, ...updatedOrder };
      this.notificationService.show('Számla sztornózva.', 'success');
    }, err => {
      this.cancellingInvoice = false;
      this.notificationService.show(`Sztornó sikertelen! Részletek: ${err.error.message}`, 'danger');
    });
  }

  // ---------------------------------------------------------------------------
  // Cseretermék (csere csomag) — a prototípus draft / replacements logikája,
  // a tényleges címkegenerálás a Playwright-tesztelt warranty replacement flow.
  // ---------------------------------------------------------------------------

  startReplacementDraft() {
    if (!this.replacementDraft) {
      this.replacementDraft = { sel: {}, reason: null };
    }
  }

  cancelReplacementDraft() {
    this.replacementDraft = null;
  }

  pickReplacementReason(reason: string) {
    if (this.replacementDraft) {
      this.replacementDraft.reason = reason;
    }
  }

  toggleReplacementSelection(row: FulfillmentRow) {
    if (!this.replacementDraft) {
      return;
    }
    this.replacementDraft.sel[row.key] = !this.replacementDraft.sel[row.key];
  }

  isReplacementSelected(row: FulfillmentRow): boolean {
    return !!this.replacementDraft?.sel[row.key];
  }

  getReplacementSelectionCount(): number {
    if (!this.replacementDraft) {
      return 0;
    }
    return Object.values(this.replacementDraft.sel).filter(Boolean).length;
  }

  /** A rendeléshez mentett csere csomagok betöltése (order_replacement). */
  fetchReplacements() {
    this.http.get<any[]>(`orders/${this.orderId}/replacements`).subscribe((list) => {
      this.replacements = (list || []).map((replacement, index) =>
        this.toReplacementPackage(replacement, index),
      );
      this.rebuildReplacementCosts();
      this.fetchAllReplacementQuotes();
      this.recomputeFinance();
    }, () => {
      this.replacements = [];
    });
  }

  private toReplacementPackage(replacement: any, index: number): ReplacementPackage {
    const items: any[] = Array.isArray(replacement?.items) ? replacement.items : [];
    const skus = items.map((item) => `${item?.sku || ''}`).filter(Boolean);
    const reasonLabel = REPLACEMENT_REASON_LABELS[`${replacement?.reason || ''}`];
    return {
      id: Number(replacement?.id),
      title: `Csere csomag #${index + 1}` + (reasonLabel ? ` · ${reasonLabel}` : ''),
      detail: `${items.length} tétel · ${skus.join(', ')}`,
      pkg: this.pkgTypes.includes(replacement?.pkg_type) ? replacement.pkg_type : 'A14',
      weight: replacement?.weight_grams !== null && replacement?.weight_grams !== undefined
        ? `${replacement.weight_grams}`
        : '250',
      label: replacement?.label_generated === 1 || replacement?.label_generated === true,
      labelUrl: replacement?.label_url ? `${replacement.label_url}` : null,
      trackingCode: replacement?.tracking_code ? `${replacement.tracking_code}` : null,
      carrierName: replacement?.carrier_name ? `${replacement.carrier_name}` : null,
      grams: 0,
      mins: 0,
      skus,
    };
  }

  /** A csere csomagok anyag-/nyomtatási költsége a csomagolási sorok BOM adataiból. */
  private rebuildReplacementCosts() {
    this.replacements = this.replacements.map((replacement) => {
      const rows = this.fulfillmentRows.filter((row) => replacement.skus.includes(row.sku));
      return {
        ...replacement,
        grams: Math.round(rows.reduce((sum, row) => sum + row.grams, 0)),
        mins: Math.round(rows.reduce((sum, row) => sum + row.mins, 0)),
      };
    });
  }

  saveReplacementDraft() {
    const draft = this.replacementDraft;
    if (!draft || this.getReplacementSelectionCount() === 0) {
      return;
    }

    const selectedRows = this.fulfillmentRows.filter((row) => draft.sel[row.key]);
    const payload = {
      reason: REPLACEMENT_REASON_KEYS[draft.reason || 'Egyéb'] || 'egyeb',
      items: selectedRows.map((row) => ({
        component_id: row.unitSummaries[0]?.componentId ?? null,
        sku: row.sku,
        name: row.name,
        quantity: row.quantity,
      })),
      pkg_type: 'A14',
      weight_grams: 250,
    };

    this.http.post(`orders/${this.orderId}/replacements`, payload).subscribe(() => {
      this.replacementDraft = null;
      this.notificationService.show('Csere csomag létrehozva.', 'success');
      this.fetchReplacements();
    }, (err) => {
      this.notificationService.show(
        `Csere csomag létrehozása sikertelen! Részletek: ${err.error?.message || err.message}`,
        'danger',
      );
    });
  }

  /** Létrehozott csere csomag törlése (order_replacement). */
  removeReplacement(index: number) {
    const replacement = this.replacements[index];
    if (!replacement?.id) {
      return;
    }

    this.confirmDialog.open({
      title: 'Csere csomag törlése',
      message: `Biztosan törlöd ezt a csere csomagot? (${replacement.title})`,
      details: 'A csomaghoz tartozó tételek törlődnek. A már legenerált címke nem vonható vissza.',
      confirmLabel: 'Törlés',
      tone: 'danger',
      icon: 'fa-solid fa-trash'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.http.delete(`orders/${this.orderId}/replacements/${replacement.id}`).subscribe(() => {
        this.notificationService.show('Csere csomag törölve.', 'success');
        this.fetchReplacements();
      }, (err) => {
        this.notificationService.show(
          `Csere csomag törlése sikertelen! Részletek: ${err.error?.message || err.message}`,
          'danger',
        );
      });
    });
  }

  pickReplacementPkg(index: number, pkg: PkgType) {
    if (this.replacements[index]) {
      this.replacements[index].pkg = pkg;
      this.recomputeFinance();
      this.saveReplacement(index);
    }
  }

  setReplacementWeight(index: number, value: string) {
    if (this.replacements[index]) {
      this.replacements[index].weight = value.replace(/[^0-9]/g, '');
      this.replacementSave$.next(index);
      // A súly dönti el a díjsávot — friss árat kérünk a díjszabásból.
      this.fetchReplacementQuote(this.replacements[index]);
    }
  }

  /** Csere csomag módosításának mentése (típus / súly / címke állapot). */
  private saveReplacement(index: number) {
    const replacement = this.replacements[index];
    if (!replacement?.id) {
      return;
    }
    this.http.patch(`orders/${this.orderId}/replacements/${replacement.id}`, {
      pkg_type: replacement.pkg,
      weight_grams: replacement.weight === '' ? null : Number(replacement.weight),
      label_generated: replacement.label,
    }).subscribe({
      error: (err) => this.notificationService.show(
        `Csere csomag mentése sikertelen! Részletek: ${err.error?.message || err.message}`,
        'danger',
      ),
    });
  }

  getReplacementCostLine(replacement: ReplacementPackage): string {
    const shipping = this.replacementShippingEur(replacement);
    const suffix = replacement.quote
      ? ` (${replacement.quote.zoneName} · ${replacement.quote.weightBracketGrams} g sáv)`
      : ' (becsült)';
    return `Termék: ${this.formatMoney(this.replacementProductCost(replacement))}`
      + ` · Szállítás: ${this.formatMoney(shipping)}${suffix}`;
  }

  /**
   * A csere csomag valós szállítási díja EUR-ban: a SAJÁT súlya és a rendelés
   * célországa alapján a díjszabásból. Ha nincs ár, marad a doboz-becslés.
   */
  private replacementShippingEur(replacement: ReplacementPackage): number {
    if (replacement.quote && this.hufPerEur > 0) {
      return Math.round((Number(replacement.quote.totalNet) / this.hufPerEur) * 100) / 100;
    }
    return this.pkgCostOf(replacement.pkg);
  }

  /** Egy csere csomag árajánlatának frissítése a saját súlya alapján. */
  private fetchReplacementQuote(replacement: ReplacementPackage): void {
    const country = (this.order?.shipping_country || this.order?.country_iso || '').trim();
    const weight = Number(replacement?.weight) || 0;
    if (!country || weight <= 0) {
      replacement.quote = null;
      this.recomputeFinance();
      return;
    }
    this.http.get<any>(`carriers/shipping-quote?country=${encodeURIComponent(country)}&weight=${weight}`)
      .subscribe({
        next: (quote) => {
          replacement.quote = quote && Number(quote.totalNet) > 0 ? quote : null;
          this.recomputeFinance();
        },
        error: () => { replacement.quote = null; },
      });
  }

  /** Minden csere csomag árajánlatának frissítése (betöltés után). */
  private fetchAllReplacementQuotes(): void {
    (this.replacements || []).forEach((r) => this.fetchReplacementQuote(r));
  }

  private replacementProductCost(replacement: ReplacementPackage): number {
    return (replacement.grams || 0) * this.filamentCostPerGramEur(this.getDominantMaterialName())
      + (replacement.mins || 0) * this.printCostPerMinuteEur();
  }

  // ---------------------------------------------------------------------------
  // Rendelési tételek szerkesztése (hozzáadás / törlés)
  // ---------------------------------------------------------------------------

  /** Termékkereső állapota a „+ Termék hozzáadása" panelhez. */
  addItemOpen = false;
  addItemSearch = '';
  addItemResults: Array<{ id: number; name: string; sku: string }> = [];
  addItemQty = 1;
  addItemSelected: { id: number; name: string; sku: string } | null = null;
  savingItem = false;

  toggleAddItem(): void {
    this.addItemOpen = !this.addItemOpen;
    this.addItemSearch = '';
    this.addItemResults = [];
    this.addItemSelected = null;
    this.addItemQty = 1;
  }

  searchAddItem(term: string): void {
    this.addItemSearch = term;
    this.addItemSelected = null;
    const filter = (term || '').trim();
    if (filter.length < 2) {
      this.addItemResults = [];
      return;
    }
    this.http.get<any[]>(`components?filter=${encodeURIComponent(filter)}`).subscribe({
      next: (list) => {
        this.addItemResults = (list || [])
          .slice(0, 8)
          .map((c) => ({ id: c.id, name: c.name, sku: c.sku }));
      },
      error: () => { this.addItemResults = []; },
    });
  }

  pickAddItem(item: { id: number; name: string; sku: string }): void {
    this.addItemSelected = item;
    this.addItemSearch = `${item.name} · ${item.sku}`;
    this.addItemResults = [];
  }

  /** Tétel hozzáadása — pótlás, nem kerül a számlára/szállítmányba. */
  confirmAddItem(): void {
    if (!this.addItemSelected || this.savingItem) {
      return;
    }
    this.savingItem = true;
    this.http.post(`orders/${this.orderId}/products`, {
      component_id: this.addItemSelected.id,
      sku: this.addItemSelected.sku,
      name: this.addItemSelected.name,
      quantity: Math.max(1, Math.round(Number(this.addItemQty) || 1)),
    }).subscribe({
      next: () => {
        this.savingItem = false;
        this.toggleAddItem();
        this.notificationService.show('Tétel hozzáadva a rendeléshez.', 'success');
        this.fetchTheOrder();
      },
      error: (e) => {
        this.savingItem = false;
        this.notificationService.show(e?.error?.message || 'A tétel hozzáadása nem sikerült!', 'danger');
      },
    });
  }

  editingPersonalizationKey: string | null = null;
  personalizationDraft = '';

  editPersonalization(row: FulfillmentRow): void {
    this.editingPersonalizationKey = row.key;
    this.personalizationDraft = row.personalization || '';
  }

  cancelPersonalization(): void {
    this.editingPersonalizationKey = null;
    this.personalizationDraft = '';
  }

  /** Személyre szabás kézi átírása — a bolti szinkron ezután nem írja felül. */
  savePersonalization(row: FulfillmentRow): void {
    if (!row?.productId || this.savingItem) {
      return;
    }
    this.savingItem = true;
    this.http.patch(`orders/${this.orderId}/products/${row.productId}/personalization`, { personalization: this.personalizationDraft })
      .subscribe({
        next: () => {
          this.savingItem = false;
          this.cancelPersonalization();
          this.notificationService.show('Személyre szabás mentve.', 'success');
          this.fetchTheOrder();
        },
        error: (e) => {
          this.savingItem = false;
          this.notificationService.show(e?.error?.message || 'A mentés nem sikerült!', 'danger');
        },
      });
  }

  /** Tétel törlése / visszaállítása — a törölt tétel szürkén a listán marad. */
  toggleRemoveItem(row: FulfillmentRow): void {
    if (!row?.productId || this.savingItem) {
      return;
    }
    const removing = !row.removed;
    const apply = () => {
      this.savingItem = true;
      this.http.patch(`orders/${this.orderId}/products/${row.productId}/removed`, { removed: removing })
        .subscribe({
          next: () => {
            this.savingItem = false;
            this.notificationService.show(
              removing ? 'Tétel törölve — kimarad a számlából és a szállításból.' : 'Tétel visszaállítva.',
              'success',
            );
            this.fetchTheOrder();
          },
          error: (e) => {
            this.savingItem = false;
            this.notificationService.show(e?.error?.message || 'A művelet nem sikerült!', 'danger');
          },
        });
    };
    if (!removing) {
      apply();
      return;
    }
    this.confirmDialog.open({
      title: 'Tétel törlése a rendelésből?',
      message: `A(z) „${row.name}" tétel szürkén, „törölve" jelzéssel a rendelésen marad, de kimarad a számlából és a szállítmányból (ShippyPro).`,
      tone: 'danger',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
    }).subscribe((result) => {
      if (result?.confirmed) {
        apply();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Csomag adatok
  // ---------------------------------------------------------------------------

  /**
   * A doboz-opciókat az Anyagok „Csomagolás" listájából (box típusú material)
   * töltjük, hogy a rendelés-oldali csomag-választó és az Anyagok szinkronban
   * legyen. A dimenzió a material.dims mezőből jön.
   */
  private fetchBoxOptions() {
    this.http.get<Array<{ name: string; type: string; dims?: string | null; price?: number | string | null }>>('materials?filter=')
      .subscribe({
        next: (materials) => {
          // Az árakat a Pénzügyek panel használja: a doboz beszerzési ára
          // (Ft/db) és a filament ára (Ft/kg) is innen jön, nem beégetve.
          this.boxPricesHuf = {};
          this.filamentPricesHuf = {};
          (materials || []).forEach((material) => {
            const price = Number(material?.price);
            if (!material?.name || !Number.isFinite(price) || price <= 0) {
              return;
            }
            if (material.type === 'box') {
              this.boxPricesHuf[material.name] = price;
            } else if (material.type === 'filament') {
              this.filamentPricesHuf[material.name.toLowerCase()] = price;
            }
          });

          const boxes = (materials || []).filter(m => m?.type === 'box' && m?.name);
          if (!boxes.length) {
            this.recomputeFinance();
            return; // marad a fallback lista
          }
          this.pkgTypes = boxes.map(b => b.name);
          this.pkgPresets = boxes.reduce((acc, b) => {
            acc[b.name] = (b.dims || '').trim() || '—';
            return acc;
          }, {} as Record<string, string>);
          // Ha a jelenleg kiválasztott típus nincs a listában, az elsőre állunk.
          if (!this.pkgTypes.includes(this.pkgType)) {
            this.pkgType = this.pkgTypes[0];
          }
          this.recomputeFinance();
        },
        error: () => { /* hálózati hiba esetén marad a fallback lista */ },
      });
  }

  /**
   * Kalkulációs beállítások: ÁFA- és vámkulcsok országonként, gépidő ár.
   * A Pénzügyek panel ezekből számol, hogy a rendelés-szintű profit és a
   * riportok ugyanazt a kulcsot használják.
   */
  private fetchCalculationSettings(): void {
    this.http.get<{
      vatRates?: Array<{ country: string; rate: number }>;
      dutyRates?: Array<{ country: string; rate: number }>;
      printPerMin?: number;
      feeShops?: Array<{ name: string; txn: number; pay: number; fixed: number; listing: number }>;
    }>('settings/calculation').subscribe({
      next: (settings) => {
        this.feeShops = settings?.feeShops || [];
        this.vatRates = this.toRateMap(settings?.vatRates);
        this.dutyRates = this.toRateMap(settings?.dutyRates);
        const printPerMin = Number(settings?.printPerMin);
        this.printPerMinHuf = Number.isFinite(printPerMin) && printPerMin > 0 ? printPerMin : null;
        this.recomputeFinance();
      },
      error: () => { /* maradnak a beégetett tartalékértékek */ },
    });
  }

  private toRateMap(rates?: Array<{ country: string; rate: number }>): Record<string, number> {
    return (rates || []).reduce((acc, entry) => {
      const country = `${entry?.country || ''}`.trim().toUpperCase();
      const rate = Number(entry?.rate);
      if (country && Number.isFinite(rate)) {
        acc[country] = rate;
      }
      return acc;
    }, {} as Record<string, number>);
  }

  /**
   * Valós EUR/HUF árfolyam (MNB, tartalék: ECB, végső: kézi beállítás) — a
   * díjszabás Ft-ban van, a rendelés EUR-ban.
   */
  private fetchExchangeRate(): void {
    this.http.get<{ hufPerEur?: number; source?: string; rateDate?: string }>('settings/exchange-rate')
      .subscribe({
        next: (result) => {
          const rate = Number(result?.hufPerEur);
          if (Number.isFinite(rate) && rate > 0) {
            this.hufPerEur = rate;
            this.exchangeRateSource = result?.source || '';
            this.exchangeRateDate = result?.rateDate || '';
            this.recomputeFinance();
          }
        },
        error: () => { /* marad az alapérték */ },
      });
  }

  /**
   * Valós szállítási díj lekérése a díjszabásból: a rendelés célországa és a
   * megadott csomagsúly alapján. Ha nincs díjszabás vagy súly, marad a
   * doboztípus szerinti becslés.
   */
  fetchShippingQuote(): void {
    const country = (this.order?.shipping_country || this.order?.country_iso || '').trim();
    const weight = Number(this.pkgWeight) || 0;
    if (!country || weight <= 0) {
      this.shippingQuote = null;
      return;
    }
    this.http.get<any>(`carriers/shipping-quote?country=${encodeURIComponent(country)}&weight=${weight}`)
      .subscribe({
        next: (quote) => {
          // 0 Ft-os (még kitöltetlen) zónánál maradjon a becslés.
          this.shippingQuote = quote && Number(quote.totalNet) > 0 ? quote : null;
          this.recomputeFinance();
        },
        error: () => { this.shippingQuote = null; },
      });
  }

  /** Az árfolyam eredete a szállítási sor tooltipjéhez. */
  getShippingRateTooltip(): string {
    if (!this.shippingQuote) {
      return 'Becsült díj — a díjszabás nincs feltöltve ehhez az országhoz.';
    }
    const src = this.exchangeRateSource === 'MNB' ? 'MNB árfolyam'
      : this.exchangeRateSource === 'ECB' ? 'ECB árfolyam' : 'kézi árfolyam';
    return `${this.shippingQuote.totalNet} Ft (szállítás ${this.shippingQuote.shippingFee}`
      + ` + vám ${this.shippingQuote.customsFee} + felár ${this.shippingQuote.surchargeAmount})`
      + ` · ${src}: 1 EUR = ${this.hufPerEur} Ft`
      + (this.exchangeRateDate ? ` (${this.exchangeRateDate})` : '');
  }

  /** A díjszabásból jövő szállítási díj EUR-ban (a pénzügyi számításhoz). */
  private quotedShippingEur(): number | null {
    if (!this.shippingQuote || !(this.hufPerEur > 0)) {
      return null;
    }
    return Math.round((Number(this.shippingQuote.totalNet) / this.hufPerEur) * 100) / 100;
  }

  /** Ajánlott szállítási díj (EUR) egy doboztípusra; ismeretlen doboznál
   *  ésszerű alapérték (a valós díjat a carrier-becslés adja). */
  private pkgCostOf(type: PkgType): number {
    return this.pkgCosts[type] ?? 4.20;
  }

  // --- Egyedi (rendelésre gyártott) termék felismerése ---
  // A design szerint egy tétel akkor egyedi, ha az „Egyedi" termékkategóriába
  // esik; ezt a SKU/név alapján ismerjük fel (pl. 'JH-EGYEDI-001', 'egyedi').
  private looksCustom(...parts: (string | null | undefined)[]): boolean {
    const h = parts.filter(Boolean).join(' ').toLowerCase();
    return h.includes('egyedi') || h.includes('custom');
  }

  isCustomRow(row: FulfillmentRow): boolean {
    return !!row?.custom;
  }

  isCustomOrder(): boolean {
    return (this.fulfillmentRows || []).some(row => this.isCustomRow(row));
  }

  // --- Készlet-státusz + átruházás ---

  /** A komponensek szabad készlete SKU szerint — a tétel-jelölésekhez. */
  private fetchComponentStock() {
    this.http.get<any[]>('components/stock').subscribe({
      next: (rows) => {
        const map: Record<string, { id: number; stock: number; reserved: number }> = {};
        (rows || []).forEach((r) => {
          if (r?.sku) {
            map[r.sku] = { id: Number(r.id), stock: Number(r.stock) || 0, reserved: Number(r.reserved) || 0 };
          }
        });
        this.stockBySku = map;
        if (this.fulfillmentRows.length) {
          this.rebuildFulfillmentRows();
        }
      },
      error: () => { /* készlet-jelölés nélkül is működik az oldal */ },
    });
  }

  /** Átruházás modál megnyitása egy tételhez. */
  openRealloc(row: FulfillmentRow) {
    if (row?.componentId == null) {
      return;
    }
    const s = this.stockBySku[row.sku];
    this.reallocProduct = {
      id: row.componentId,
      sku: row.sku,
      name: row.name,
      stock: s?.stock ?? 0,
      reserved: s?.reserved ?? 0,
    };
  }

  onReallocSaved() {
    this.reallocProduct = null;
    this.fetchComponentStock();
    this.fetchTheOrder();
  }

  pickPkgType(pkg: PkgType) {
    this.pkgType = pkg;
    this.recomputeFinance();
    this.savePackage();
  }

  setPkgWeight(value: string) {
    this.pkgWeight = value.replace(/[^0-9]/g, '');
    this.pkgWeightTouched = true;
    this.packageSave$.next();
    // A súly dönti el a díjsávot — friss árat kérünk.
    this.fetchShippingQuote();
  }

  /** Mezőelhagyáskor azonnal mentünk (a debounce-olt mentést nem várjuk meg). */
  flushPackageSave() {
    this.savePackage();
  }

  /** Csere csomag súly mezőjének elhagyásakor azonnali mentés. */
  flushReplacementSave(index: number) {
    this.saveReplacement(index);
  }

  /**
   * Csomag adatok betöltése a rendelésről. Ha még nincs mentett érték, a súly
   * a BOM alapján számított összsúly (a típus alapértelmezése A14).
   */
  private initPackageData() {
    const savedType = this.order?.package_type as PkgType;
    if (savedType && this.pkgTypes.includes(savedType)) {
      this.pkgType = savedType;
    }

    if (this.pkgWeightTouched) {
      return;
    }
    const savedWeight = this.order?.package_weight_grams;
    if (savedWeight !== null && savedWeight !== undefined) {
      this.pkgWeight = `${savedWeight}`;
      return;
    }
    const totalGrams = Math.round(this.fulfillmentRows.reduce((sum, row) => sum + row.grams, 0));
    this.pkgWeight = totalGrams > 0 ? `${totalGrams}` : '';
  }

  /** Csomag adatok (típus + súly) mentése a rendelésre. */
  private savePackage() {
    if (!(this.orderId > 0)) {
      return;
    }
    this.http.patch(`orders/${this.orderId}/package`, {
      package_type: this.pkgType,
      package_weight_grams: this.pkgWeight === '' ? null : Number(this.pkgWeight),
    }).subscribe({
      next: (updatedOrder: any) => {
        this.order = { ...this.order, ...(updatedOrder || {}) };
      },
      error: (err) => this.notificationService.show(
        `Csomag adatok mentése sikertelen! Részletek: ${err.error?.message || err.message}`,
        'danger',
      ),
    });
  }

  // ---------------------------------------------------------------------------
  // Pénzügyek — valós order adatból számítva; ami nem elérhető az
  // adatmodellben (Etsy/Jump díjak, ÁFA), az kimarad (backend igény).
  // ---------------------------------------------------------------------------

  toggleFin() {
    this.finOpen = !this.finOpen;
  }

  recomputeFinance() {
    if (!this.order) {
      this.finRows = [];
      this.profitValue = '';
      this.profitPct = '';
      this.profitNegative = false;
      return;
    }

    const rows: FinanceRow[] = [];
    const products = this.order?.orderProducts || [];

    const productCount = products.reduce((sum, product) => sum + (Number(product.quantity) || 0), 0);
    let productsRevenue = products.reduce((sum, product) => {
      const unitPrice = Number(product.discounted_price ?? product.price ?? product.original_price) || 0;
      return sum + unitPrice * (Number(product.quantity) || 1);
    }, 0);

    const shippingRevenue = Number(this.order?.shipping_cost) || 0;

    if (!productsRevenue && this.order?.price) {
      productsRevenue = Math.max((Number(this.order.price) || 0) - shippingRevenue, 0);
    }

    rows.push({
      label: `Termékek (${productCount} db)`,
      value: this.formatMoney(productsRevenue),
      negative: false,
    });

    if (shippingRevenue > 0) {
      rows.push({
        label: 'Szállítás (vevő fizette)',
        value: this.formatMoney(shippingRevenue),
        negative: false,
      });
    }

    // --- ÁFA: a vevő bruttó árat fizet, abban benne van a célország ÁFA-ja.
    // Nettó árbevétel = bruttó / (1 + kulcs); a különbözet nem a miénk.
    const country = this.destinationCountry();
    const grossRevenue = productsRevenue + shippingRevenue;
    const vatRate = this.rateFor(this.vatRates, country);
    const vatAmount = vatRate > 0
      ? Math.round((grossRevenue - grossRevenue / (1 + vatRate / 100)) * 100) / 100
      : 0;
    if (vatAmount > 0) {
      rows.push({
        label: `ÁFA (${country} ${this.formatPercent(vatRate)})`,
        value: `−${this.formatMoney(vatAmount)}`,
        negative: true,
      });
    }
    const netRevenue = grossRevenue - vatAmount;

    // --- Vám: a nettó árbevételre vetítve, a célországhoz beállított kulccsal.
    const dutyRate = this.rateFor(this.dutyRates, country);
    const dutyAmount = dutyRate > 0 ? Math.round(netRevenue * dutyRate) / 100 : 0;
    if (dutyAmount > 0) {
      rows.push({
        label: `Vám (${country} ${this.formatPercent(dutyRate)})`,
        value: `−${this.formatMoney(dutyAmount)}`,
        negative: true,
      });
    }

    // --- Szállítási címke. Ha van díjszabás-ajánlat, külön mutatjuk a
    // vámkezelési díjat, hogy ne olvadjon bele a címke árába.
    const labelCost = this.quotedShippingEur() ?? this.pkgCostOf(this.pkgType);
    const customsHandling = this.quotedCustomsEur();
    rows.push({
      label: `${this.getRecommendedCarrierName()} címke`,
      value: `−${this.formatMoney(labelCost - customsHandling)}`,
      negative: true,
    });
    if (customsHandling > 0) {
      rows.push({
        label: 'Vámkezelési díj (futár)',
        value: `−${this.formatMoney(customsHandling)}`,
        negative: true,
      });
    }

    // --- Csomagolóanyag: a kiválasztott doboz beszerzési ára (Anyagok).
    const boxCost = this.boxCostEur(this.pkgType);
    if (boxCost > 0) {
      rows.push({
        label: `Csomagolóanyag (${this.pkgType} · ${this.boxPricesHuf[this.pkgType]} Ft/db)`,
        value: `−${this.formatMoney(boxCost)}`,
        negative: true,
      });
    }

    const activeRows = this.fulfillmentRows.filter((row) => !row.removed && row.componentId);
    this.ensureUnitCosts(activeRows.map((row) => Number(row.componentId)));
    const costs = activeRows.map((row) => ({ row, cost: this.unitCosts[Number(row.componentId)] }));
    let materialCost: number;
    let printCost: number;
    let boughtCost = 0;
    if (activeRows.length && costs.every((c) => !!c.cost)) {
      // A recept szerinti önköltség (ugyanaz, mint a Készlet oldalon): nyomtatott anyag + gépidő + vásárolt tételek.
      const sum = (f: 'material' | 'machine' | 'bought' | 'grams' | 'minutes') =>
        costs.reduce((acc, c) => acc + c.cost[f] * (Number(c.row.quantity) || 1), 0);
      const toEur = (huf: number) => (this.hufPerEur > 0 ? Math.round((huf / this.hufPerEur) * 100) / 100 : 0);
      materialCost = toEur(sum('material'));
      printCost = toEur(sum('machine'));
      boughtCost = toEur(sum('bought'));
      const missing = costs.some((c) => c.cost.missing);
      const estimated = costs.some((c) => c.cost.estimated);
      rows.push({
        label: `Nyomtatás – anyag (${Math.round(sum('grams'))} g${estimated ? ' · becsült anyagár' : ''})`,
        value: `−${this.formatMoney(materialCost)}`,
        negative: true,
      });
      rows.push({
        label: `Nyomtatás – gépidő + áram (${Math.round(sum('minutes'))} perc)`,
        value: `−${this.formatMoney(printCost)}`,
        negative: true,
      });
      rows.push({
        label: `Vásárolt alkatrészek (mágnes, higrométer, plexi…)${missing ? ' · VAN ÁR NÉLKÜLI TÉTEL' : ''}`,
        value: `−${this.formatMoney(boughtCost)}`,
        negative: true,
      });
    } else {
      const totalGrams = Math.round(this.fulfillmentRows.reduce((sum, row) => sum + row.grams, 0));
      const totalMins = Math.round(this.fulfillmentRows.reduce((sum, row) => sum + row.mins, 0));
      const materialName = this.getDominantMaterialName();
      materialCost = this.materialCostEur();
      printCost = totalMins * this.printCostPerMinuteEur();
      rows.push({
        label: `Anyagköltség (${totalGrams} g${materialName ? ' ' + materialName : ''} · ${this.materialPriceLabel(materialName)})`,
        value: `−${this.formatMoney(materialCost)}`,
        negative: true,
      });
      rows.push({
        label: `Nyomtatás + áram (${totalMins} perc)`,
        value: `−${this.formatMoney(printCost)}`,
        negative: true,
      });
    }

    // --- Webshop-díjak (Etsy: tranzakciós + fizetési % a teljes összegre, fix díj, listázási díj tételenként).
    // Etsy-rendelés (mindkét Etsy-bolt) az Etsy díjait viseli; más boltnál a bolt neve dönt.
    const shopFee = this.shopFeeFor(this.order?.merchant_id === 1
      ? 'Etsy'
      : (this.order?.merchant?.name || this.resolveMerchantName(this.order?.merchant_id)));
    let platformFee = 0;
    if (shopFee) {
      platformFee = Math.round((grossRevenue * ((Number(shopFee.txn) || 0) + (Number(shopFee.pay) || 0)) / 100
        + (Number(shopFee.fixed) || 0) + (Number(shopFee.listing) || 0) * productCount) * 100) / 100;
      if (platformFee > 0) {
        rows.push({
          label: `${shopFee.name} díjak (${this.formatPercent((Number(shopFee.txn) || 0) + (Number(shopFee.pay) || 0))} + fix + listázás)`,
          value: `−${this.formatMoney(platformFee)}`,
          negative: true,
        });
      }
    }

    let replacementCostTotal = 0;
    this.replacements.forEach((replacement, index) => {
      const productCost = this.replacementProductCost(replacement);
      const shipCost = this.replacementShippingEur(replacement);
      // A csere csomag dobozának beszerzési ára is valós költség.
      const replacementBoxCost = this.boxCostEur(replacement.pkg);
      replacementCostTotal += productCost + shipCost + replacementBoxCost;
      rows.push({
        label: `Csere csomag #${index + 1} · termék (${replacement.grams} g · ${replacement.mins} perc)`,
        value: `−${this.formatMoney(productCost)}`,
        negative: true,
      });
      rows.push({
        label: `Csere csomag #${index + 1} · szállítás + doboz (${replacement.pkg})`,
        value: `−${this.formatMoney(shipCost + replacementBoxCost)}`,
        negative: true,
      });
    });

    const profit = netRevenue
      - dutyAmount - labelCost - boxCost - materialCost - printCost - boughtCost - platformFee - replacementCostTotal;
    // A fedezethányad a nettó (ÁFA nélküli) árbevételre vetítve értelmes.
    const profitBase = netRevenue;
    const profitPctNum = profitBase > 0 ? (profit / profitBase) * 100 : 0;

    this.finRows = rows;
    this.profitValue = this.formatMoney(profit);
    this.profitPct = profitPctNum.toFixed(1).replace('.', ',') + '%';
    this.profitNegative = profit < 0;
    this.finNote = this.buildFinanceNote(country, vatRate, dutyRate, grossRevenue, netRevenue);
  }

  /**
   * A számítás magyarázata a ténylegesen alkalmazott kulcsokkal — így a
   * kezelő látja, miből jött ki a nettó árbevétel, és hol állítható a kulcs.
   */
  private buildFinanceNote(
    country: string,
    vatRate: number,
    dutyRate: number,
    grossRevenue: number,
    netRevenue: number,
  ): string {
    const parts: string[] = [];
    if (vatRate > 0) {
      parts.push(
        `A vevő bruttó árat fizet: ${this.formatMoney(grossRevenue)} ÷ ${(1 + vatRate / 100)
          .toFixed(2).replace('.', ',')} = ${this.formatMoney(netRevenue)} nettó`
        + ` (${country} ÁFA ${this.formatPercent(vatRate)}).`,
      );
    } else if (country) {
      parts.push(`${country}-hoz nincs ÁFA-kulcs beállítva, a bevétel nettóként számol.`);
    }
    if (dutyRate > 0) {
      parts.push(`Vám: a nettó árbevétel ${this.formatPercent(dutyRate)}-a.`);
    }
    parts.push('Anyag- és nyomtatási költség a komponensek súlyából (g) és nyomtatási idejéből;'
      + ' a csomagolóanyag ára az Anyagok listájából jön.');
    parts.push('Az ÁFA- és vámkulcsok a Beállításokban módosíthatók.');
    return parts.join(' ');
  }

  /** A rendelés célországa (ISO kód) — az ÁFA- és vámkulcs ehhez tartozik. */
  private destinationCountry(): string {
    return `${this.order?.shipping_country || this.order?.country_iso || ''}`.trim().toUpperCase();
  }

  /**
   * Kulcs a célországhoz. Ha az országhoz nincs külön sor, a '*' jelű
   * alapértelmezett kulcs érvényes; ha az sincs, 0 (nincs levonás).
   */
  private rateFor(rates: Record<string, number>, country: string): number {
    const rate = rates[country] ?? rates['*'];
    return Number.isFinite(rate) && rate > 0 ? rate : 0;
  }

  private formatPercent(value: number): string {
    return `${(Math.round(value * 100) / 100).toString().replace('.', ',')}%`;
  }

  /** A díjszabás-ajánlatban szereplő vámkezelési díj EUR-ban (0, ha nincs). */
  private quotedCustomsEur(): number {
    if (!this.shippingQuote || !(this.hufPerEur > 0)) {
      return 0;
    }
    const customs = Number(this.shippingQuote.customsFee) || 0;
    return customs > 0 ? Math.round((customs / this.hufPerEur) * 100) / 100 : 0;
  }

  /** A kiválasztott doboz beszerzési ára EUR-ban (Anyagok → Csomagolás). */
  private boxCostEur(type: PkgType): number {
    const priceHuf = Number(this.boxPricesHuf[type]) || 0;
    if (priceHuf <= 0 || !(this.hufPerEur > 0)) {
      return 0;
    }
    return Math.round((priceHuf / this.hufPerEur) * 100) / 100;
  }

  /**
   * Filament ár EUR/g-ban a tétel anyaga szerint (Anyagok → Filament, Ft/kg).
   * Ha az anyaghoz nincs ár, a beégetett tartalékérték marad.
   */
  private filamentCostPerGramEur(materialName?: string | null): number {
    const priceHufPerKg = Number(this.filamentPricesHuf[`${materialName || ''}`.toLowerCase()]) || 0;
    if (priceHufPerKg <= 0 || !(this.hufPerEur > 0)) {
      return this.MATERIAL_COST_PER_GRAM;
    }
    return priceHufPerKg / 1000 / this.hufPerEur;
  }

  /** A rendelés tételeinek önköltség-bontása a backendről (egyszer tételsoronként). */
  private ensureUnitCosts(ids: number[]): void {
    const key = [...new Set(ids)].sort((a, b) => a - b).join(',');
    if (!key || key === this.unitCostsKey) {
      return;
    }
    this.unitCostsKey = key;
    this.http.get<OrderDetailsComponent['unitCosts']>(`components/unit-costs?ids=${key}`).subscribe({
      next: (costs) => {
        this.unitCosts = { ...this.unitCosts, ...(costs || {}) };
        this.recomputeFinance();
      },
      error: () => { /* marad a régi, súly alapú becslés */ },
    });
  }

  private shopFeeFor(merchantName: string | null | undefined) {
    const key = `${merchantName || ''}`.trim().toLowerCase();
    if (!key || !this.feeShops.length) {
      return null;
    }
    return this.feeShops.find((shop) => `${shop.name}`.toLowerCase() === key)
      || this.feeShops.find((shop) => key.includes(`${shop.name}`.toLowerCase()) || `${shop.name}`.toLowerCase().includes(key))
      || null;
  }

  /** Anyagköltség tételenként, a saját filamentjének árával számolva. */
  private materialCostEur(): number {
    const cost = this.fulfillmentRows.reduce((sum, row) => {
      return sum + row.unitSummaries.reduce((unitSum, unit) => {
        const grams = (unit.weightGrams || 0) * unit.quantity;
        return unitSum + grams * this.filamentCostPerGramEur(unit.materialName);
      }, 0);
    }, 0);
    return Math.round(cost * 100) / 100;
  }

  /** Gépidő ár EUR/perc (Beállítások → printPerMin, Ft/perc). */
  private printCostPerMinuteEur(): number {
    if (this.printPerMinHuf && this.hufPerEur > 0) {
      return this.printPerMinHuf / this.hufPerEur;
    }
    return this.PRINT_COST_PER_MINUTE;
  }

  /** A Pénzügyek sorában megjelenő filament egységár (pl. „3300 Ft/kg"). */
  private materialPriceLabel(materialName: string | null): string {
    const priceHufPerKg = Number(this.filamentPricesHuf[`${materialName || ''}`.toLowerCase()]) || 0;
    return priceHufPerKg > 0 ? `${priceHufPerKg} Ft/kg` : '3200 Ft/kg (becsült)';
  }

  private getDominantMaterialName(): string | null {
    for (const row of this.fulfillmentRows) {
      for (const unit of row.unitSummaries) {
        if (unit.materialName) {
          return unit.materialName;
        }
      }
    }
    return null;
  }

  formatMoney(amount: number): string {
    const currency = this.order?.currency_code || 'EUR';
    return amount.toFixed(2).replace('.', ',') + ' ' + currency;
  }

  // ---------------------------------------------------------------------------
  // Üzenőfal — OrderMessagesService (üres/404 állapot kezelve)
  // ---------------------------------------------------------------------------

  fetchMessages() {
    this.loadingMessages = true;
    this.orderMessagesService.getMessages(this.orderId).subscribe((messages) => {
      this.messages = messages || [];
      this.loadingMessages = false;
    }, () => {
      // A backend végpont még hiányozhat (404) — üres üzenőfalat mutatunk.
      this.messages = [];
      this.loadingMessages = false;
    });
  }

  sendMessage() {
    const text = this.msgInput.trim();
    if (!text || this.sendingMessage) {
      return;
    }
    this.sendingMessage = true;
    this.orderMessagesService.sendMessage(this.orderId, text).subscribe((message) => {
      this.sendingMessage = false;
      this.msgInput = '';
      this.messages = [...this.messages, message];
    }, (err) => {
      this.sendingMessage = false;
      this.notificationService.show(`Üzenet küldése sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  deleteMessage(message: OrderMessage) {
    if (this.deletingMessageIds.has(message.id)) {
      return;
    }
    this.deletingMessageIds.add(message.id);
    this.orderMessagesService.deleteMessage(this.orderId, message.id).subscribe(() => {
      this.deletingMessageIds.delete(message.id);
      this.messages = this.messages.filter((m) => m.id !== message.id);
    }, (err) => {
      this.deletingMessageIds.delete(message.id);
      this.notificationService.show(`Üzenet törlése sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  getMessageInitials(author: string): string {
    return (author || '?').slice(0, 2).toUpperCase();
  }

  formatMessageTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const now = new Date();
    const hm = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    if (date.toDateString() === now.toDateString()) {
      return `ma ${hm}`;
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `tegnap ${hm}`;
    }
    return `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${hm}`;
  }

  // ---------------------------------------------------------------------------
  // Csomagolási sorok felépítése
  // ---------------------------------------------------------------------------

  private rebuildFulfillmentRows() {
    const previousExpandedRows = { ...this.expandedRows };
    const remainingPackagedById = new Map<number, number>();
    // Komponens nélküli tételek pipái — a rendelési tétel azonosítója szerint.
    const remainingPackagedByProductId = new Map<number, number>();

    (this.order?.packagingComponentList || []).forEach((component) => {
      if (!component?.packaged) {
        return;
      }
      if (component.order_product_id) {
        remainingPackagedByProductId.set(
          component.order_product_id,
          (remainingPackagedByProductId.get(component.order_product_id) || 0) + 1,
        );
      } else if (component.id) {
        remainingPackagedById.set(component.id, (remainingPackagedById.get(component.id) || 0) + 1);
      }
    });

    const rows = (this.order?.orderProducts || []).map((product, index) => {
      // A backend csomagolási listáját tükrözzük: csak a komponenssel bíró
      // BOM-levelek egységek; ha nincs ilyen (nincs SKU, vagy nincs fent a
      // rendszerben), a tétel maga az egység, és a rendelési tétel
      // azonosítójával pipálható.
      const rawUnits = this.collectLeafUnits(product.childrenComponents).filter((unit) => !!unit.componentId);
      const groupedUnits = this.groupUnits(
        rawUnits.length ? rawUnits : [{
          orderProductId: product.id ?? undefined,
          name: this.resolveProductName(product),
          sku: product.sku || '',
          quantity: Number(product.quantity) || 1,
          imageFile: product.component?.image_file
        }],
      );

      const unitSummaries = groupedUnits.map((unit) => {
        let availablePackaged = 0;
        if (unit.componentId) {
          availablePackaged = remainingPackagedById.get(unit.componentId) || 0;
        } else if (unit.orderProductId) {
          availablePackaged = remainingPackagedByProductId.get(unit.orderProductId) || 0;
        }
        const packagedQuantity = Math.min(unit.quantity, availablePackaged);

        if (unit.componentId) {
          remainingPackagedById.set(unit.componentId, Math.max(availablePackaged - packagedQuantity, 0));
        } else if (unit.orderProductId) {
          remainingPackagedByProductId.set(unit.orderProductId, Math.max(availablePackaged - packagedQuantity, 0));
        }

        return {
          ...unit,
          packagedQuantity
        };
      });

      const totalRequired = unitSummaries.reduce((sum, unit) => sum + unit.quantity, 0);
      const totalPackaged = unitSummaries.reduce((sum, unit) => sum + unit.packagedQuantity, 0);
      const rowName = this.resolveProductName(product);
      const rowQuantity = Number(product.quantity) || 1;
      const firstUnit = unitSummaries[0];
      const canExpand = !!unitSummaries.length && (
        unitSummaries.length > 1
        || firstUnit?.name !== rowName
        || firstUnit?.quantity !== rowQuantity
      );

      const rowSku = product.sku || product.component?.sku || 'Nincs SKU';
      const stockInfo = this.stockBySku[rowSku];
      const availableStock = stockInfo ? stockInfo.stock : null;
      // Készlet-hiány jelölés csak a még nem teljesített rendeléseknél releváns.
      const showStock = !this.isPackagingCompleted() && availableStock !== null;
      const stockOut = showStock && availableStock <= 0;
      const lastPiece = showStock && availableStock === 1;

      return {
        key: `${product.id || product.component_id || index}-${index}`,
        productId: product.id ?? null,
        removed: !!(product as any).removed_at,
        isAdded: (product as any).is_added === 1,
        name: rowName,
        sku: rowSku,
        quantity: rowQuantity,
        imageFile: product.component?.image_file || firstUnit?.imageFile,
        unitSummaries,
        // Ha a rendelés teljesítve/feladva, a tétel készként (zöld pipa) jelenik
        // meg akkor is, ha nincs külön csomagolási rekord (pl. importált vagy
        // külső csatornán feladott rendelésnél).
        isPackaged: this.isPackagingCompleted() || (totalRequired > 0 && totalPackaged === totalRequired),
        canExpand,
        canTogglePackaging: unitSummaries.some((unit) => !!unit.componentId || !!unit.orderProductId),
        componentId: product.component_id ?? stockInfo?.id,
        availableStock,
        stockOut,
        lastPiece,
        // Egyedi, ha a komponens is_custom jelölésű, vagy a név/SKU alapján az.
        custom: !!product.component?.is_custom || this.looksCustom(rowSku, rowName),
        personalization: this.sanitizeText(product.personalization),
        grams: unitSummaries.reduce((sum, unit) => sum + (unit.weightGrams || 0) * unit.quantity, 0),
        mins: unitSummaries.reduce((sum, unit) => sum + (unit.printMinutes || 0) * unit.quantity, 0),
      } as FulfillmentRow;
    });

    const nextExpandedRows: Record<string, boolean> = {};
    rows.forEach((row) => {
      if (row.unitSummaries.length && previousExpandedRows[row.key]) {
        nextExpandedRows[row.key] = true;
      }
    });

    if (!Object.keys(previousExpandedRows).length) {
      const firstExpandableRow = rows.find((row) => row.canExpand);
      if (firstExpandableRow) {
        nextExpandedRows[firstExpandableRow.key] = true;
      }
    }

    this.fulfillmentRows = rows;
    this.expandedRows = nextExpandedRows;
  }

  private collectLeafUnits(node?: ComponentBomDescriptor): FulfillmentUnitDraft[] {
    if (!node) {
      return [];
    }

    const isPackageUnit = node.component?.is_package_unit === 1;

    // Mirror the backend packaging parser so the UI toggles the exact same
    // package units that the server uses for persisted packaging state.
    if (!isPackageUnit && node.children?.length) {
      return node.children.flatMap((child) => this.collectLeafUnits(child));
    }

    return [{
      componentId: node.component?.id,
      name: node.component?.name || 'Ismeretlen komponens',
      sku: node.component?.sku || 'Nincs SKU',
      quantity: Number(node.quantity) || 1,
      imageFile: node.component?.image_file,
      weightGrams: Number(node.component?.weight) || 0,
      printMinutes: Number(node.component?.printing_time) || 0,
      materialName: node.component?.material?.name || null,
    }];
  }

  private groupUnits(units: FulfillmentUnitDraft[]): FulfillmentUnitDraft[] {
    const groupedUnits = new Map<string, FulfillmentUnitDraft>();

    units.forEach((unit, index) => {
      const key = unit.componentId
        ? `c${unit.componentId}`
        : unit.orderProductId ? `p${unit.orderProductId}` : `${unit.sku}-${unit.name}-${index}`;
      const existingUnit = groupedUnits.get(key);

      if (existingUnit) {
        existingUnit.quantity += unit.quantity;
        return;
      }

      groupedUnits.set(key, { ...unit });
    });

    return Array.from(groupedUnits.values());
  }

  private resolveProductName(product: OrderItemWithBomView): string {
    return product?.name || product?.component?.name || product?.sku || 'Ismeretlen termék';
  }

  getUnitMeta(unit: FulfillmentUnitSummary): string {
    const parts = [
      unit.sku,
      `${unit.quantity} db`,
    ];
    if (unit.weightGrams) {
      parts.push(`${Math.round(unit.weightGrams * unit.quantity)} g`);
    }
    if (unit.printMinutes) {
      parts.push(`${Math.round(unit.printMinutes * unit.quantity)} perc`);
    }
    if (unit.packagedQuantity > 0 && unit.packagedQuantity < unit.quantity) {
      parts.push(`${unit.packagedQuantity}/${unit.quantity} kész`);
    }
    return parts.filter(Boolean).join(' · ');
  }

  formatUnitLabel(unit: FulfillmentUnitSummary): string {
    const qty = Number(unit.quantity) || 0;
    const baseName = this.sanitizeText(unit.name) || 'Ismeretlen komponens';
    const sku = this.sanitizeText(unit.sku);
    if (sku && !baseName.toLowerCase().includes(sku.toLowerCase())) {
      return `${qty}x ${baseName} - ${sku}`;
    }
    return `${qty}x ${baseName}`;
  }

  private sanitizeText(value?: string | null): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const normalized = value.trim();
    return normalized.length ? normalized : null;
  }

  private resolveMerchantName(merchantId?: number): string {
    switch (merchantId) {
      case 1:
        return 'Etsy';
      case 2:
        return 'Shopify';
      case 3:
        return 'WooCommerce';
      case 4:
        return 'Philibert';
      default:
        return 'Shop';
    }
  }

  private isEtsyOrder(): boolean {
    return this.order?.source_key === 'etsy' || this.order?.merchant_id === 1;
  }

}
